import styles from "./style.module.scss";
import cx from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { Button, Col, Input, Row, Table } from "reactstrap";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import moment from "moment";
import { formatMoneyValue } from "@utils/Currency";
import DropdownActionsMenu from "@components/content/DropdownMenu";
import { useAppSelector } from "@/store/hooks";
import { ClientStatusRowData, InterestConfigType, normalizeClientStatusRow } from "@utils/DocumentStatus";
import { e } from "mathjs";
import { showAlert } from "@utils/Alert";
import ActionConfirmation from "@components/content/ActionConfirmation";
import { ConfirmActionPubSub, DocumentStatusListPubSub, DocumentStatusListPubSubPayload, OpenAddCreditAmountPubSub, OpenChangeDocumentPlazoPubSub } from "@utils/pubsubs/PubSub";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProviderInvoiceFormDataKeys } from "@/generated/ProviderInvoiceFormDataKeys";
import { ProviderInvoiceRelatedAttributes } from "@/formTypes/ProviderInvoiceDataType";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import ChangeDocumentPlazoOffCanvas from "@components/layout/Offcanvas/ChangeDocumentPlazoOffCanvas";
import { BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { ClientCreditApi, ClientCreditDataType } from "@/api/ClientCreditApi";
import AddCreditAmountOffCanvas from "@components/layout/Offcanvas/AddCreditAmountOffCanvas";
import { CLIENT_CREDIT_CANCELLED_STATUS } from "@utils/Constants";
import { CreditAmountTarget } from "@utils/CreditAmount";

export type DocumentsEntryType =
  | { kind: "invoice"; data: InvoiceDataType }
  | { kind: "settlement"; data: SettlementDataType }
  | { kind: "providerInvoice"; data: ProviderInvoiceDataType }
  | { kind: "buyInvoice"; data: BuyInvoiceDataType }
  | { kind: "clientCredit"; data: ClientCreditDataType };

type DocumentStatusListProps = {
  rows: DocumentsEntryType[];
  projectData: string,
  useToggle?: boolean,
  preventMultipleCurrency?: boolean,
  onRefreshDataRequired?: () => void,
  onDocumentUpdate?: (document: ClientStatusRowData) => void,
  onDocumentToogle?: (documents: DocumentsEntryType[], interestExemptedDocuments: string[]) => void
}

const DocumentStatusList = (props: DocumentStatusListProps) => {
  const { rows = [], projectData, useToggle = false, onDocumentToogle, onDocumentUpdate, onRefreshDataRequired, preventMultipleCurrency = false } = props;
  // const { interest_on_due, is_interest_on_due = false, interest_product_id }: SuscriptionDataType =
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { interest_on_due, is_interest_on_due = false } =
    useAppSelector(state => state.suscription?.suscription || {})
  const [areAllInvoiceSelected, setAllInvoiceSelected] = useState<boolean>(false);
  const [documentSelected, setDocumentSelected] = useState<string[]>([]);
  const [interestExemptedDocuments, setInterestExemptedDocuments] = useState<string[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<number | null>();
  const [normalizedData, setNormalizedData] = useState<ClientStatusRowData[]>([]);

  useEffect(() => {
    const unsubscribe = DocumentStatusListPubSub.subscribe(PerformDocumentStatusPubsub);
    return () => {
      unsubscribe();
    };
  }, [])

  const PerformDocumentStatusPubsub = (payload: DocumentStatusListPubSubPayload) => {
    const { action } = payload;
    if (action == "Clear") {
      setSelectedCurrency(null)
    }
  }

  useEffect(() => {
    setInterestExemptedDocuments([])
    const interestConfig: InterestConfigType = {
      interestOnDue: Number(interest_on_due || 0),
      isInterestOnDue: is_interest_on_due || false,
      interestExemptedDocuments
    }
    normalizeData(interestConfig)

  }, [rows])

  const normalizeData = async (interestConfig: InterestConfigType) => {
    const normalizedData: ClientStatusRowData[] = await Promise.all(rows.map(async (r) => { return await normalizeClientStatusRow(projectData, r, interestConfig) }));
    setNormalizedData(normalizedData)
  }

  const toggleDocument = (item: ClientStatusRowData, event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    let newDocumentList
    if (checked) {
      if (preventMultipleCurrency) {
        if (selectedCurrency == null) {
          setSelectedCurrency(item.currency?.currency_id)
        } else if (selectedCurrency !== item.currency?.currency_id) {
          showAlert("danger", "No es posible crear un recibo mezclando facturas de 2 diferentes monedas");
          return
        }
      }
      newDocumentList = [
        ...documentSelected,
        `${item.id}`
      ]
      setDocumentSelected(newDocumentList)
    } else {
      const documents = documentSelected.filter(d => d != `${item.id}`)
      if (documents.length == 0) {
        setSelectedCurrency(null)
      }
      newDocumentList = documents
      setDocumentSelected(documents)
    }

    triggerOnDocumentToogle(rows.filter(r => newDocumentList.includes(`${r.data.id}`)), interestExemptedDocuments)
  }

  const triggerOnDocumentToogle = (rows: DocumentsEntryType[], interestExemptedDocuments: string[]) => {
    if (onDocumentToogle) {
      onDocumentToogle(rows, interestExemptedDocuments)
    }
  }

  const toggleAllProductLine = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      if (preventMultipleCurrency && normalizedData.length > 0) {
        const firstCurrency = normalizedData[0].currency?.currency_id;
        if (!normalizedData.every(item => item.currency?.currency_id === firstCurrency)) {
          showAlert("danger", "No es posible crear un recibo mezclando facturas de 2 diferentes monedas");
          return
        }
      }
      setDocumentSelected(rows.map(r => `${r.data.id}`));
      triggerOnDocumentToogle(rows, interestExemptedDocuments)
    } else {
      setDocumentSelected([]);
      setSelectedCurrency(null)
      triggerOnDocumentToogle([], interestExemptedDocuments)
    }
    setAllInvoiceSelected(checked)
  }

  const addInterestExemptedDocument = (documentId: string) => {
    setInterestExemptedDocuments([
      ...interestExemptedDocuments,
      documentId
    ])
  }

  const removeInterestExemptedDocument = (documentId: string) => {
    setInterestExemptedDocuments(interestExemptedDocuments.filter(d => d != documentId))
  }

  const confirmMarkInvoiceAsPayied = (document: ClientStatusRowData) => {
    ConfirmActionPubSub.publish({
      openOffCanvas: true,
      confirmText: `Esta seguro que desea marca la factura #${document.numero} como Pagada? esta accion no se puede revertir`,
      onConfirmCalback: async () => {
        try {
          setIsLoading(true)
          const invoice = await InvoiceApi.get(document.id, { selectionSet: [...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k))] })
          await InvoiceApi.update(`${invoice.id}`, {
            ...invoice,
            pagada: true,
            status: "pagada"
          })
          showAlert("success", `La factura #${document.numero} se ha marcado como pagada`)
          if (onDocumentUpdate) {
            onDocumentUpdate(document)
          }
        } catch (err) {
          if (err instanceof Error) {
            showAlert("danger", err.message)
          }
        } finally {
          setIsLoading(false)
        }
      }
    })
  }

  const openCreditAmountOffCanvas = (documents: ClientStatusRowData[]) => {
    const [firstDocument] = documents;
    if (!firstDocument) {
      return
    }
    const targets: CreditAmountTarget[] = documents.map(d => ({
      id: d.id,
      numero: d.numero,
      saldo: d.saldo,
      kind: d.kind == "settlement" ? "settlement" : "invoice"
    }));

    OpenAddCreditAmountPubSub.publish({
      openOffCanvas: true,
      clientId: `${firstDocument.clientId || ""}`,
      clientNombre: firstDocument.nombreFacturacion,
      documents: targets,
      currency: firstDocument.currency,
      date: projectData
    })
  }

  const confirmCancelClientCredit = (document: ClientStatusRowData) => {
    ConfirmActionPubSub.publish({
      openOffCanvas: true,
      confirmText: `Esta seguro que desea anular el saldo a favor ${document.numero}? esta accion no se puede revertir`,
      onConfirmCalback: async () => {
        try {
          setIsLoading(true)
          await ClientCreditApi.update(`${document.id}`, {
            estado: CLIENT_CREDIT_CANCELLED_STATUS
          })
          showAlert("success", `El saldo a favor ${document.numero} fue anulado`)
          triggerRefetch()
        } catch (err) {
          if (err instanceof Error) {
            showAlert("danger", err.message)
          }
        } finally {
          setIsLoading(false)
        }
      }
    })
  }

  const openChangePlazoOffCanvas = (document: ClientStatusRowData) => {
    OpenChangeDocumentPlazoPubSub.publish({
      openOffCanvas: true,
      documentId: document.id,
      documentType: document.kind == "invoice" ? 'invoice' : "settlement",
      plazo: document.plazo
    })
  }

  const confirmMarkProviderInvoiceAsPayied = (document: ClientStatusRowData) => {
    ConfirmActionPubSub.publish({
      openOffCanvas: true,
      confirmText: `Esta seguro que desea marca la factura #${document.sequence} como Pagada? esta accion no se puede revertir`,
      onConfirmCalback: async () => {
        try {
          setIsLoading(true)
          const invoice = await ProviderInvoiceApi.get(document.id, { selectionSet: [...ProviderInvoiceFormDataKeys.filter(k => !ProviderInvoiceRelatedAttributes.includes(k))] })
          await ProviderInvoiceApi.update(`${invoice.id}`, {
            ...invoice,
            pagada: true,
            status: "pagada"
          })
          showAlert("success", `La factura #${document.sequence} se ha marcado como pagada`)
          if (onDocumentUpdate) {
            onDocumentUpdate(document)
          }
        } catch (err) {
          if (err instanceof Error) {
            showAlert("danger", err.message)
          }
        } finally {
          setIsLoading(false)
        }
      }
    })
  }

  const renderProductLine = (): ReactNode[] => {
    return normalizedData.map(r => {
      const date = moment(r.fecha, "YYYY-MM-DD")
      const formatDate = date.format("DD-MM-YYYY")
      const currencyId = Number(r.currency.currency_id || 1);

      const isInterestExempted = interestExemptedDocuments.includes(r.id)

      return (
        <tr
          key={r.id}
          className={cx({
            [styles.hasNote]: r.hasNote,
            [styles.hasExtraPayment]: r.hasPayment,
            [styles.interestSettlement]: r.interestExempt,
            [styles.clientCredit]: r.kind == "clientCredit"
          })}
        >
          {useToggle ? (
            <td>
              <Input
                className={styles.selectCheckbox}
                data-testid={`document-select-${r.id}`}
                type="checkbox"
                checked={documentSelected.includes(`${r.id}`)}
                onChange={(e) => toggleDocument(r, e)}
              />
            </td>
          ) : ""}
          <td className="text-center">{r.numero}</td>
          <td>{r.sequence}</td>
          <td>{r.nombreFacturacion}</td>
          <td>{formatDate}</td>
          <td>{formatMoneyValue(r.total, currencyId)}</td>
          <td>{formatMoneyValue(r.saldo, currencyId)}</td>
          <td className="text-center">{r.diasFacturado} dias</td>
          <td className="text-center">{r.diasVencida} dias</td>
          {!!is_interest_on_due && (<td className="text-center"> {formatMoneyValue(r.interest, currencyId)}</td>)}
          <td>{r.recibo ?? "-"}</td>
          <td>
            <DropdownActionsMenu
              actions={[
                {
                  isVisible: (r.kind == "invoice" || r.kind == "settlement") && !!is_interest_on_due && !isInterestExempted && !r.pagada && r.interest > 0,
                  actionTitle: "Eliminar Intereses",
                  onClick: () => addInterestExemptedDocument(r.id)
                },
                {
                  isVisible: (r.kind == "invoice" || r.kind == "settlement") && !!is_interest_on_due && isInterestExempted && !r.pagada && r.interest > 0,
                  actionTitle: "Agregar Intereses",
                  onClick: () => removeInterestExemptedDocument(r.id)
                },
                {
                  isVisible: (r.kind == "invoice" || r.kind == "settlement") && !!is_interest_on_due,
                  actionTitle: "Ver Intereses generados",
                  href: `/user/interest/${r.kind == "invoice" ? "byInvoice" : "bySettlement"}/${r.id}`,
                  target: "_blank"
                },
                {
                  isVisible: r.kind == "invoice" || r.kind == "provider-invoice" || r.kind == "buyInvoice",
                  actionTitle: "Ver Factura",
                  href: `/user/${r.kind}s/view/${r.id}`,
                  target: "_blank"
                },
                {
                  isVisible: r.kind == "invoice" && r.hasNote,
                  actionTitle: "Ver Notas de Credito",
                  href: `/user/creditnotes/listByInvoiceId/${r.id}`,
                  target: "_blank"
                },
                {
                  isVisible: r.kind == "provider-invoice" && r.hasNote,
                  actionTitle: "Ver Notas de Credito",
                  href: `/user/provider-invoices/notes/${r.id}`,
                  target: "_blank"
                },
                {
                  // isVisible: r.kind == "provider-invoice" && r.hasNote && r.saldo <= 0,
                  isVisible: r.kind == "invoice" || r.kind == "settlement",
                  actionTitle: "Cambiar Plazo Credito",
                  onClick: () => openChangePlazoOffCanvas(r),
                },
                {
                  isVisible: (r.kind == "invoice" || r.kind == "settlement") && !r.pagada && r.saldo > 0,
                  actionTitle: "Agregar Monto a Favor",
                  onClick: () => openCreditAmountOffCanvas([r]),
                },
                {
                  isVisible: r.kind == "clientCredit",
                  actionTitle: "Anular Saldo a Favor",
                  onClick: () => confirmCancelClientCredit(r),
                },
                {
                  // isVisible: r.kind == "provider-invoice" && r.hasNote && r.saldo <= 0,
                  isVisible: r.kind == "provider-invoice",
                  actionTitle: "Marca como pagada",
                  onClick: () => confirmMarkProviderInvoiceAsPayied(r),
                },
                {
                  // isVisible: r.kind == "invoice" && r.saldo <= 0,
                  isVisible: r.kind == "invoice",
                  actionTitle: "Marca como pagada",
                  onClick: () => confirmMarkInvoiceAsPayied(r),
                },
                {
                  isVisible: r.kind == "invoice" && r.hasNote,
                  actionTitle: "Ver Notas de Debito",
                  href: `/user/debitnotes/listByInvoiceId/${r.id}`,
                  target: "_blank"
                },
                {
                  isVisible: !!r.recibo && !!r.reciboId,
                  actionTitle: "Ver Recibo",
                  href: r.kind == "provider-invoice" || r.kind == "buyInvoice" ?
                    `/user/provide-recibes/view/${r.reciboId}` :
                    `/user/recibes/view/${r.reciboId}`,
                  target: "_blank"
                },
                {
                  isVisible: r.hasPayment,
                  actionTitle: "Ver Historial de Abonos",
                  href: `/user/${r.kind}s/payments/${r.id}`,
                  target: "_blank"
                },
                {
                  isVisible: r.kind == "settlement",
                  actionTitle: "Ver Liquidacion",
                  href: `/user/settlements/view/${r.id}`,
                  target: "_blank"
                },
              ]}
            />
          </td>
        </tr>
      )
    });
  }

  const onCreditAmountCreated = () => {
    // El monto a favor cambia los saldos: se limpia la seleccion y se recarga.
    setDocumentSelected([]);
    setAllInvoiceSelected(false);
    setSelectedCurrency(null);
    triggerOnDocumentToogle([], interestExemptedDocuments);
    triggerRefetch();
  }

  const triggerRefetch = () => {
    if (onRefreshDataRequired) {
      onRefreshDataRequired()
    }
  }

  const marcarPagadas = async () => {
    const updatePromises = normalizedData
      .filter(nd => nd.saldo <= 0 && nd.kind != "clientCredit")
      .map(p => {
        if (p.kind == "invoice") {
          return InvoiceApi.update(`${p.id}`, {
            clientId: p.clientId,
            pagada: true,
            status: "pagada"
          })
        } else if (p.kind == "settlement") {
          return SettlementApi.update(`${p.id}`, {
            clientId: p.clientId,
            pagada: true,
            status: "pagada"
          })
        } else {
          return ProviderInvoiceApi.update(`${p.id}`, {
            providerId: p.providerId,
            pagada: true,
            status: "pagada"
          })
        }
      })
    await Promise.all(updatePromises)
  }

  return (
    <>
      {is_interest_on_due && (
        <Row>
          <Col xs="10" md="6">
            <div className="d-flex mb-2">
              <Col xs="1" className={styles.interestSettlement}>&nbsp;</Col>
              <Col className="ps-2" xs="11">Liquidacion de Intereses</Col>
            </div>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs="10" md="6">
          <div className="d-flex mb-2">
            <Col xs="1" className={styles.hasNote}>&nbsp;</Col>
            <Col className="ps-2" xs="11">Facturas con nota de credito o Debito</Col>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs="10" md="6">
          <div className="d-flex mb-2">
            <Col xs="1" className={styles.hasExtraPayment}>&nbsp;</Col>
            <Col className="ps-2" xs="11">Facturas con Abonos</Col>
          </div>
        </Col>
      </Row>
      {normalizedData.some(r => r.kind == "clientCredit") && (
        <Row>
          <Col xs="10" md="6">
            <div className="d-flex mb-2">
              <Col xs="1" className={styles.clientCredit}>&nbsp;</Col>
              <Col className="ps-2" xs="11">Saldos a Favor del Cliente</Col>
            </div>
          </Col>
        </Row>
      )}

      <Button hidden onClick={marcarPagadas}>
        marca como pagada en 0
      </Button>


      <Table
        responsive
        className={styles.productTitle}
        striped>
        <thead className={styles.tableHeader}>
          <tr>
            {useToggle ? (
              <td>
                <Input
                  className={styles.selectCheckbox}
                  data-testid="document-select-all"
                  type="checkbox"
                  checked={areAllInvoiceSelected}
                  onChange={(e) => toggleAllProductLine(e)}
                />
              </td>) : ""
            }
            <td className={styles.numFact}># Factura</td>
            <td className={styles.sequence}>Consecutivo</td>
            <td className={styles.invoiceName}>Nombre Facturacion</td>
            <td className={styles.fecha}>Fecha</td>
            <td className={styles.total}>Total</td>
            <td className={styles.saldo}>Saldo</td>
            <td className={styles.factDays}>Dias Facturado</td>
            <td className={styles.expiredDays}>Dias Vencida</td>
            {!!is_interest_on_due && (<td className={styles.expiredDays}>Intereses Generados</td>)}
            <td className={styles.recibe}>Recibo</td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          {renderProductLine()}
        </tbody>
      </Table>
      <ActionConfirmation
        title="Confirmar"
      />
      <LoadingScreen isLoading={isLoading} />
      <ChangeDocumentPlazoOffCanvas onPlazoChanged={triggerRefetch} />
      <AddCreditAmountOffCanvas onCreditAmountCreated={onCreditAmountCreated} />
    </>
  )
}

export default DocumentStatusList;  